// Calendar primitives: the day/week block model, as first-class structure.
//
// The plan's availability model is dated morning/afternoon/evening BLOCKS, not a
// continuous time grid (section 12; the schema at 4874 has exactly these three). So
// the lane is the primitive, and Epic 5's availability entry reuses these components
// rather than growing its own grid.
//
// The accessibility requirement is specific and shapes the markup: "Availability grid
// has a list/field representation and understandable labels such as `Tuesday
// afternoon, selected`" (section 24.4). A visual grid of coloured squares is not an
// acceptable implementation — so a selectable lane is a real toggle button carrying
// its day context in its accessible name, and lane contents are a real list.
//
// No copy lives here: every label is caller-supplied from @herefor/product-language.

import type { ReactNode } from "react";

import { cx } from "./styles";

/** The three blocks. Identifiers only — the words come from product-language. */
export const DAY_BLOCK_IDS = ["morning", "afternoon", "evening"] as const;
export type DayBlockId = (typeof DAY_BLOCK_IDS)[number];

export interface DayBlock {
  readonly id: DayBlockId;
  /** Display text for this block, caller-supplied (e.g. "Afternoon"). */
  readonly label: string;
  /** Rendered contents — typically StateChips or need summaries. */
  readonly children?: ReactNode;
  /**
   * Present only when the lane is selectable (Epic 5 availability entry). Omit it and
   * the lane is a plain read-only row, which is what the plan surfaces need.
   */
  readonly selected?: boolean;
  readonly onToggle?: (id: DayBlockId) => void;
  /**
   * Overrides the composed accessible name. Supply this from product-language when a
   * locale needs a different word order than "<day>, <block>".
   */
  readonly accessibleName?: string;
}

export interface CalendarDayProps {
  /** Stable identifier, e.g. an ISO date. Never displayed. */
  readonly dayId: string;
  /** Display text for the day, caller-supplied (e.g. "Tuesday 12 March"). */
  readonly dayLabel: string;
  readonly blocks: readonly DayBlock[];
  /** Marks the day as past. Presentation only — the caller decides what "past" means. */
  readonly past?: boolean;
}

function DayBlockLane({ block, dayLabel }: { block: DayBlock; dayLabel: string }) {
  const items = block.children;
  const selectable = block.onToggle !== undefined;

  const laneBody = (
    <ul className={cx.calendarLaneItems}>
      {/* A real list, so a screen reader announces how many things are in this
          block rather than reading a run-on of chips. */}
      {items !== undefined && items !== null ? <li>{items}</li> : null}
    </ul>
  );

  if (!selectable) {
    return (
      <div className={cx.calendarLane}>
        <span className={cx.calendarLaneLabel}>{block.label}</span>
        {laneBody}
      </div>
    );
  }

  // "Tuesday 12 March, Afternoon" — the day context travels with the control, because
  // a screen-reader user arriving on the button by keyboard has no visual column to
  // tell them which day they are in.
  const name = block.accessibleName ?? `${dayLabel}, ${block.label}`;

  return (
    <button
      type="button"
      className={`${cx.calendarLane} ${cx.button}`}
      // aria-pressed carries "selected" without inventing a word for it — the screen
      // reader supplies "pressed"/"not pressed" in the user's own language.
      aria-pressed={block.selected === true}
      aria-label={name}
      onClick={() => {
        block.onToggle?.(block.id);
      }}
    >
      <span className={cx.calendarLaneLabel} aria-hidden="true">
        {block.label}
      </span>
      {laneBody}
    </button>
  );
}

/** One day, with its three blocks as lanes. */
export function CalendarDay({ dayId, dayLabel, blocks, past = false }: CalendarDayProps) {
  return (
    <section className={cx.calendarDay} data-day={dayId} data-past={past ? "true" : undefined}>
      <h3 className={cx.calendarDayHeader}>{dayLabel}</h3>
      {blocks.map((block) => (
        <DayBlockLane key={block.id} block={block} dayLabel={dayLabel} />
      ))}
    </section>
  );
}

export interface CalendarWeekProps {
  /** Accessible name for the week region, caller-supplied. */
  readonly label: string;
  readonly days: readonly CalendarDayProps[];
}

/**
 * A week of days.
 *
 * A `<section>` with an accessible name rather than a `<table>`: the block model is a
 * list of days each containing three named lanes, not tabular data, and table
 * semantics would force screen-reader users through row/column navigation that says
 * nothing useful here.
 */
export function CalendarWeek({ label, days }: CalendarWeekProps) {
  return (
    <section className={cx.calendarWeek} aria-label={label}>
      {days.map((day) => (
        <CalendarDay key={day.dayId} {...day} />
      ))}
    </section>
  );
}
